import React from "react";

import { Stream } from "@/apollo/types";
import { Button } from "@/components/elements/Button";
import { CronDisplayReadable } from "@/components/elements/CronDisplayReadable";
import Tooltip from "@/components/elements/Tooltip";
import { DefaultOptions } from "@/components/modules/WeldSyncScheduler";
import { SourceStream } from "@/features/elt";
import { DeprecatedHoverCard } from "@/features/elt/deprecated-hover-card/DeprecatedHoverCard";
import cn from "@/helpers/classNames";
import { ComponentProps } from "@tw-classed/react";
import { SquarePenIcon, TriangleAlert } from "lucide-react";
import { StreamMode } from "../types/StreamMode";
import { OptionTooltips } from "./OptionTooltips";

const ModeTooltip = (props: React.ComponentProps<typeof Tooltip>) => (
  <Tooltip {...props} maxWidth="300px" placement="top-end">
    <span className="leading-none">{props.children}</span>
  </Tooltip>
);

export const ModeLabel = ({
  editable,
  tooltip,
  ...buttonProps
}: ComponentProps<typeof Button> & {
  editable?: boolean;
  tooltip?: JSX.Element;
}) => {
  if (editable) {
    return (
      <ModeTooltip content={tooltip || null}>
        <Button
          size="xs"
          {...buttonProps}
          className={cn(
            "pointer-events-auto cursor-pointer",
            buttonProps.className,
          )}
          iconRight={<SquarePenIcon />}
          onClick={buttonProps.onClick}
        />
      </ModeTooltip>
    );
  }
  return (
    <ModeTooltip content={tooltip || null}>
      <Button
        size="xs"
        {...buttonProps}
        {...({
          as: "div",
          type: undefined,
        } as any)}
        className={cn("pointer-events-none shadow-none", buttonProps.className)}
      />
    </ModeTooltip>
  );
};

export const SyncLabels = (props: {
  stream: Stream;
  streamState?: SourceStream;
  mode: StreamMode;
  onIncrementalClick: () => void;
}) => {
  const deprecatedLabel = props.stream.retired ? (
    <DeprecatedHoverCard retirementDetails={props.stream.retirementDetails}>
      <DeprecatedLabel />
    </DeprecatedHoverCard>
  ) : null;

  const cdcLabel = props.streamState?.changeDataCaptureEnabled ? (
    <ModeLabel tooltip={OptionTooltips.changeDataCapture}>CDC</ModeLabel>
  ) : null;

  const commonLabels = (
    <>
      {deprecatedLabel}
      {cdcLabel}
    </>
  );

  const fullSyncSchedule = props.streamState?.fullSyncSchedule;
  // The picker's own wording for the schedules it offers, which is far shorter than spelling the
  // cron out; anything hand-written falls back to the readable expression.
  const presetSchedule = Object.values(DefaultOptions).find(
    (option) => option.value === fullSyncSchedule,
  );
  const fullSyncScheduleLabel = fullSyncSchedule ? (
    <ModeLabel
      tooltip={
        <p>
          Full sync schedule:{" "}
          <CronDisplayReadable verbose cron={fullSyncSchedule} />
        </p>
      }
    >
      Full sync{" "}
      {presetSchedule ? (
        presetSchedule.label.toLowerCase()
      ) : (
        <CronDisplayReadable cron={fullSyncSchedule} />
      )}
    </ModeLabel>
  ) : null;

  const protectedLabel = props.streamState?.protectedFromFullSync ? (
    <ModeLabel tooltip={OptionTooltips.protectedShort}>Protected</ModeLabel>
  ) : null;

  const softDeleteLabel = props.streamState?.softDeleteEnabled ? (
    <ModeLabel tooltip={OptionTooltips.softDeleteShort}>
      Captures deletes
    </ModeLabel>
  ) : null;

  switch (props.mode) {
    case StreamMode.FIXED_FULL:
      return (
        <>
          {commonLabels}
          {softDeleteLabel}
          <ModeLabel tooltip={OptionTooltips.fullShort}>Full</ModeLabel>
        </>
      );
    case StreamMode.MANUAL_FULL:
      return (
        <>
          {commonLabels}
          {softDeleteLabel}
          <ModeLabel tooltip={OptionTooltips.full}>Full</ModeLabel>
        </>
      );
    case StreamMode.CDC:
      return commonLabels;
    case StreamMode.INCREMENTAL:
      return (
        <>
          {commonLabels}
          {fullSyncScheduleLabel}
          {protectedLabel}
          {softDeleteLabel}

          <ModeLabel
            editable={props.stream.configurableIncremental}
            onClick={props.onIncrementalClick}
            tooltip={
              props.stream.configurableIncremental
                ? OptionTooltips.incremental
                : OptionTooltips.incrementalShort
            }
          >
            Incremental
          </ModeLabel>
        </>
      );
    case StreamMode.APPEND:
      return (
        <>
          {commonLabels}
          {fullSyncScheduleLabel}
          {protectedLabel}
          {softDeleteLabel}
          <ModeLabel
            editable={props.stream.configurableIncremental}
            onClick={props.onIncrementalClick}
            tooltip={OptionTooltips.append}
          >
            Append
          </ModeLabel>
        </>
      );
  }
};

export const DeprecatedLabel = React.forwardRef<
  HTMLSpanElement,
  React.ComponentPropsWithoutRef<"span">
>(({ className, ...props }, ref) => (
  <span ref={ref} {...props} className={cn("leading-none", className)}>
    <ModeLabel icon={<TriangleAlert />} colorScheme="warning">
      Deprecated
    </ModeLabel>
  </span>
));
DeprecatedLabel.displayName = "DeprecatedLabel";
